import PropTypes from 'prop-types';
import React, {Component, Children} from 'react';
import _ from 'lodash';
import {TweenLite, Power2} from 'gsap';
import Draggable from 'gsap/Draggable';
import * as Colors from 'material-ui/styles/colors';
import Paper from 'material-ui/Paper';
import transitions from 'material-ui/styles/transitions';

const GRID_TILE_CLASS_NAME = 'absolute-grid-tile';
const ANIMATION_DURATION = 0.3;
const ANIMATION_IN_PROGRESS = 'true';
const ANIMATION_NOT_IN_PROGRESS = 'false';
const ORDER_CHANGE_THRESHOLD = 0.8;

export default class AbsoluteGrid extends Component {
  constructor(props) {
    super(props);
    this.state = {
      draggedElementKey: null,
    };
    this.draggableItems = null;
    this.gridTileClassName = _.uniqueId(GRID_TILE_CLASS_NAME);
  }

  componentDidMount() {
    this.updateDraggableItems(true);
  }

  componentDidUpdate(prevProps) {
    const {children, columns} = this.props;
    if (Children.count(children) !== Children.count(prevProps.children) || !prevProps.columns) {
      this.updateDraggableItems(true);
    } else if (
      columns !== prevProps.columns ||
      !_.isEmpty(_.filter(children, (child, order) => child.key !== prevProps.children[order].key))
    ) {
      this.updateDraggableItems();
    }
  }

  onDragStart(absoluteGridRef) {
    const {children} = absoluteGridRef.props;
    const targetIntOrder = parseInt(this.order, 10);
    const draggedElementKey = children[targetIntOrder].key;
    absoluteGridRef.setState({draggedElementKey});
  }

  onDrag(absoluteGridRef) {
    const to = absoluteGridRef.getElementOrderByPosition(this.x, this.y, this.order);
    absoluteGridRef.updateItemsPositions(this.order, to);
  }

  onDragEnd(absoluteGridRef) {
    const {onChangeOrder} = absoluteGridRef.props;
    const from = absoluteGridRef.getElementOrderByPosition(this.startX, this.startY);
    const to = absoluteGridRef.getElementOrderByPosition(this.endX, this.endY, this.order);
    const {x, y} = absoluteGridRef.getElementPositionByOrder(to);
    TweenLite.to(this.target, ANIMATION_DURATION, {x, y});
    absoluteGridRef.updateItemsPositions(this.order, to);
    if (from !== to && _.isFunction(onChangeOrder)) {
      onChangeOrder(from, to);
    }
    absoluteGridRef.setState({draggedElementKey: null});
  }

  getColumnByPosition = (x, originalColumn) => {
    const {elementWidth, margin, columns} = this.props;
    let column = x / (elementWidth + margin);
    const columnDelta = originalColumn - column;
    if (originalColumn !== null) {
      if (Math.abs(columnDelta) > ORDER_CHANGE_THRESHOLD) {
        column = columnDelta > 0 ? originalColumn - 1 : originalColumn + 1;
      } else {
        column = originalColumn;
      }
    }
    column = column >= columns ? columns - 1 : column;
    return column;
  };

  getRowByPosition = (y, originalRow) => {
    const {elementHeight, margin} = this.props;
    let row = y / (elementHeight + margin);
    const rowDelta = originalRow - row;
    if (originalRow !== null) {
      if (Math.abs(rowDelta) > ORDER_CHANGE_THRESHOLD) {
        row = rowDelta > 0 ? originalRow - 1 : originalRow + 1;
      } else {
        row = originalRow;
      }
    }
    return row;
  };

  getElementOrderByPosition = (x, y, originalOrder = null) => {
    const {columns} = this.props;
    const childrenCount = Children.count(this.props.children);
    const originalColumn = originalOrder !== null ? this.getColumnByOrder(originalOrder) : null;
    const column = this.getColumnByPosition(x, originalColumn);
    const originalRow = originalOrder !== null ? this.getRowByOrder(originalOrder) : null;
    const row = this.getRowByPosition(y, originalRow);
    const order = column + row * columns;
    if (order < 0) {
      return 0;
    }
    if (order > childrenCount - 1) {
      return childrenCount - 1;
    }
    return Math.round(order);
  };

  getColumnByOrder = order => {
    return order % this.props.columns;
  };

  getRowByOrder = order => {
    return Math.floor(order / this.props.columns);
  };

  getElementPositionByOrder = order => {
    const {elementHeight, elementWidth, margin} = this.props;
    const column = this.getColumnByOrder(order);
    const row = this.getRowByOrder(order);
    return {x: column * (elementWidth + margin), y: row * (margin + elementHeight)};
  };

  getRootElementStyle() {
    const {columns, elementHeight, margin, children} = this.props;
    if (!columns) {
      return {opacity: 0};
    }
    const childrenCount = Children.count(children);
    const rows = Math.ceil(childrenCount / columns);
    return {
      height: rows * (margin + elementHeight),
      width: '100%',
      opacity: 1,
      position: 'relative', // TODO check if need this
    };
  }

  updateDraggableItems = (noAnimation = false) => {
    const {disableDragAndDrop} = this.props;
    if (!disableDragAndDrop) {
      this.draggableItems = Draggable.create(`.${this.gridTileClassName}`, {
        cursor: 'pointer',
        onDragStart: this.onDragStart,
        onDragStartParams: [this],
        onDrag: this.onDrag,
        onDragParams: [this],
        onDragEnd: this.onDragEnd,
        onDragEndParams: [this],
        dragClickables: true,
        allowContextMenu: true,
        zIndexBoost: false,
      });
      const animationDuration = noAnimation ? 0 : ANIMATION_DURATION;
      _.forEach(this.draggableItems, (draggable, order) => {
        draggable.order = order;
        draggable.animationInProgress = ANIMATION_NOT_IN_PROGRESS;
        const {x, y} = this.getElementPositionByOrder(order);
        TweenLite.to(draggable.target, animationDuration, {x, y});
      });
    }
  };

  updateItemsPositions = (from, to) => {
    if (from !== to) {
      const {margin, elementWidth} = this.props;
      this.draggableItems.splice(to, 0, this.draggableItems.splice(from, 1)[0]);
      _.forEach(this.draggableItems, (draggable, order) => {
        const prevOrder = draggable.order;
        draggable.order = order;
        if (order !== to) {
          const prevRow = this.getRowByOrder(prevOrder);
          const currRow = this.getRowByOrder(order);
          if (prevRow !== currRow) {
            const amountToMove = prevRow > currRow ? (elementWidth + margin) / 2 : -(elementWidth + margin) / 2;
            const startPos = this.getElementPositionByOrder(prevOrder);
            const {x, y} = this.getElementPositionByOrder(order);
            const toAnimation = TweenLite.fromTo(draggable.target, ANIMATION_DURATION / 2, startPos, {
              x: startPos.x - amountToMove,
              opacity: 0,
            });
            draggable.animationInProgress = ANIMATION_IN_PROGRESS;
            const delay = toAnimation.duration() - toAnimation.time();
            const fromAnimation = TweenLite.fromTo(
              draggable.target,
              ANIMATION_DURATION / 2,
              {x: x + amountToMove, y},
              {x, opacity: 1, delay}
            );
            setTimeout(
              () => (draggable.animationInProgress = ANIMATION_NOT_IN_PROGRESS),
              (fromAnimation.duration() - fromAnimation.time()) * 1000
            );
          } else if (draggable.animationInProgress === ANIMATION_NOT_IN_PROGRESS) {
            const {x} = this.getElementPositionByOrder(order);
            TweenLite.to(draggable.target, ANIMATION_DURATION, {x});
          }
        }
      });
    }
  };

  render() {
    const {children, elementHeight, elementWidth, disableDragAndDrop, style} = this.props;
    const {draggedElementKey} = this.state;
    const rootStyle = this.getRootElementStyle();
    return (
      <div style={{...style, ...rootStyle, transition: transitions.easeOut('75ms', 'opacity')}}>
        {Children.map(children, (child, order) => {
          const {x, y} = this.getElementPositionByOrder(order);
          const {left, top} = disableDragAndDrop ? {left: x, top: y} : {left: undefined, top: undefined};
          const zDepth = draggedElementKey === child.key ? 3 : 0;
          return [
            <div
              style={{
                top: y,
                left: x,
                position: 'absolute',
                backgroundColor: Colors.grey100,
                height: elementHeight,
                width: elementWidth,
                zIndex: 0,
              }}
            />,
            <Paper
              className={this.gridTileClassName}
              style={{
                left,
                top,
                position: 'absolute',
                zIndex: 1,
                transition: transitions.easeOut(undefined, 'box-shadow'),
              }}
              key={`abs-grid-${child.key}`}
              zDepth={zDepth}
            >
              {child}
            </Paper>,
          ];
        })}
      </div>
    );
  }
}

AbsoluteGrid.propTypes = {
  elementHeight: PropTypes.number.isRequired,
  elementWidth: PropTypes.number.isRequired,
  columns: PropTypes.number,
  margin: PropTypes.number.isRequired,
  onChangeOrder: PropTypes.func.isRequired,
  disableDragAndDrop: PropTypes.bool,
  children: PropTypes.array.isRequired,
};
